import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const formatPrice = (price) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(price);
};

const NearMePanel = ({ userLocation, properties, onClose, geocoding = false }) => {
  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';

  const hasNearby = properties.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
              <span>📍</span>
              <span>{t('nearMe.title')}</span>
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {geocoding
                ? t('nearMe.calculating')
                : hasNearby
                ? `${t('nearMe.found')} (${properties.length})`
                : t('nearMe.noNearby')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/80 hover:bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Geocoding Progress Banner */}
        {geocoding && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center space-x-3 text-sm">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
            <p className="text-blue-800 font-medium">{t('nearMe.calculating')}</p>
          </div>
        )}

        {/* Property List */}
        <div className="p-6">
          {geocoding && !hasNearby ? (
            <div className="text-center py-10">
              <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">{t('common.loading')}</p>
            </div>
          ) : hasNearby ? (
            <div className="space-y-3">
              {properties.map((property, index) => {
                const title = isVietnamese && property.titleVi ? property.titleVi : property.title;
                const districtName = isVietnamese && property.districtVi ? property.districtVi : property.district;
                const cityName = isVietnamese && property.cityVi ? property.cityVi : property.city;

                return (
                  <Link
                    key={property.id}
                    to={`/housing/${property.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-all group border border-gray-100 hover:border-purple-200 hover:shadow-sm"
                  >
                    {/* Rank Number */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                      {index + 1}
                    </div>

                    {/* Image */}
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={property.images[0]}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm group-hover:text-purple-600 transition-colors truncate">
                        {title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {districtName}, {cityName}
                      </p>
                      <p className="text-xs font-medium text-purple-600 mt-1">
                        {formatPrice(property.price)}
                        <span className="text-gray-400 font-normal"> /tháng</span>
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10">
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-gray-500">{t('nearMe.noNearby')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NearMePanel;
